import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Offcanvas from 'react-bootstrap/Offcanvas'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'

import { postAiReportPlan } from '../../utilities/api'
import {
  AI_REPORT_UNMAPPABLE_MESSAGE,
  isAiReportAssistantEnabled,
} from './aiReportAssistant'
import {
  filtersWithShowUnverified,
  showUnverifiedFromFilters,
} from './customReportHelpers'

import './askForReportPanel.scss'

export default function AskForReportPanel({
  organizationUuid,
  organizationName,
  from,
  to,
  rangeLabel,
  onOpenInBuilder,
  nested = false,
  integrated = false,
  onApplyToBuilder,
  onPlanChange,
}) {
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [planning, setPlanning] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [planError, setPlanError] = useState(null)
  const [plan, setPlan] = useState(null)
  const [answer, setAnswer] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [showUnverified, setShowUnverified] = useState(true)
  const requestSeqRef = useRef(0)

  const enabled = isAiReportAssistantEnabled()

  useEffect(() => {
    if (plan?.filters) {
      setShowUnverified(showUnverifiedFromFilters(plan.filters))
    }
  }, [plan])

  const effectiveFilters = useMemo(() => {
    if (!plan?.filters) return null
    return filtersWithShowUnverified(plan.filters, showUnverified)
  }, [plan?.filters, showUnverified])

  const effectivePlan = useMemo(() => {
    if (!plan || !effectiveFilters) return null
    return { ...plan, filters: effectiveFilters }
  }, [plan, effectiveFilters])

  const clearAnswerState = useCallback(() => {
    setPlan(null)
    setAnswer(null)
    setPlanError(null)
    setLoadError(null)
    if (onPlanChange) onPlanChange(null)
  }, [onPlanChange])

  const handleSubmit = useCallback(async () => {
    const trimmed = prompt.trim()
    if (!trimmed) return
    const requestId = ++requestSeqRef.current
    setPlanError(null)
    setLoadError(null)
    setAnswer(null)
    setPlan(null)
    setPlanning(true)
    try {
      const res = await postAiReportPlan({
        prompt: trimmed,
        organizationUUID: organizationUuid,
        from,
        to,
        rangeLabel,
      })
      if (requestId !== requestSeqRef.current) return

      const nextPlan = res?.data?.plan || null
      const nextAnswer = res?.data?.answer || null
      setPlan(nextPlan)
      setAnswer(
        nextAnswer?.text
          ? nextAnswer
          : nextPlan
            ? {
                text: 'Could not load an answer for that question.',
              }
            : null
      )
      if (onPlanChange) {
        onPlanChange(nextPlan)
      }
    } catch (err) {
      if (requestId !== requestSeqRef.current) return
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        AI_REPORT_UNMAPPABLE_MESSAGE
      setPlan(null)
      setAnswer(null)
      setPlanError(msg)
      if (onPlanChange) {
        onPlanChange(null)
      }
    } finally {
      if (requestId === requestSeqRef.current) {
        setPlanning(false)
      }
    }
  }, [prompt, organizationUuid, from, to, rangeLabel, onPlanChange])

  const canBuildReport =
    plan && (plan.columnIds?.length > 0 || plan.aggregations?.length > 0)

  const handleGenerate = useCallback(async () => {
    if (!canBuildReport) return
    setLoadError(null)
    setGenerating(true)
    try {
      const planToApply = effectivePlan || plan
      if (integrated && onApplyToBuilder) {
        await onApplyToBuilder(planToApply, {
          answerText: answer?.text || '',
          summary: answer?.summary || null,
        })
        clearAnswerState()
        setOpen(false)
        return
      }
      if (onOpenInBuilder) {
        onOpenInBuilder(planToApply, {
          answerText: answer?.text || '',
          summary: answer?.summary || null,
        })
      }
    } catch (err) {
      setLoadError(
        err?.response?.data?.error?.message ||
          err?.message ||
          'Failed to load report preview'
      )
    } finally {
      setGenerating(false)
    }
  }, [
    plan,
    effectivePlan,
    answer,
    integrated,
    onApplyToBuilder,
    onOpenInBuilder,
    canBuildReport,
    clearAnswerState,
  ])

  if (!enabled) {
    return null
  }

  return (
    <div
      className={`ask-for-report-panel${nested ? ' ask-for-report-panel--nested' : ''}`}
      data-testid="ask-for-report-panel"
    >
      <Button
        type="button"
        variant="primary"
        size="sm"
        className="ask-for-report-panel__trigger"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        data-testid="ask-for-report-toggle"
      >
        Ask for a report
      </Button>

      <Offcanvas
        show={open}
        onHide={() => setOpen(false)}
        placement="end"
        className="ask-for-report-panel__drawer"
        data-testid="ask-for-report-drawer"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Ask for a report</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="ask-for-report-panel__body">
          <Form
            onSubmit={(e) => {
              e.preventDefault()
              void handleSubmit()
            }}
          >
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="e.g. How much net revenue did we make yesterday?"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="ask-for-report-prompt"
            />
            <Stack direction="horizontal" className="mt-2 gap-2 flex-wrap">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!prompt.trim()}
                data-testid="ask-for-report-submit"
              >
                {planning ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-2"
                      role="status"
                    />
                    Answering…
                  </>
                ) : answer ? (
                  'Ask again'
                ) : (
                  'Ask'
                )}
              </Button>
              {answer || planError ? (
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={clearAnswerState}
                  disabled={planning}
                  data-testid="ask-for-report-reset"
                >
                  Clear answer
                </Button>
              ) : null}
            </Stack>
          </Form>

          {planError ? (
            <Alert
              variant="warning"
              className="mt-3 mb-0"
              data-testid="ask-for-report-plan-error"
            >
              {planError}
            </Alert>
          ) : null}

          {answer && !planning ? (
            <div
              className="ask-for-report-panel__answer mt-3"
              data-testid="ask-for-report-answer"
            >
              <div className="ask-for-report-panel__answer-text">
                {String(answer.text || '')
                  .split('\n')
                  .map((line, index) => (
                    <p
                      key={index}
                      className={index === 0 ? 'mb-1' : 'mb-0 small'}
                    >
                      {line}
                    </p>
                  ))}
              </div>
            </div>
          ) : null}

          {answer && plan && !planning ? (
            <div
              className="ask-for-report-panel__offer mt-3"
              data-testid="ask-for-report-offer"
            >
              <p className="mb-2 fw-semibold">
                Would you like a report with this data?
              </p>
              <Stack direction="horizontal" gap={2} className="flex-wrap mb-2">
                <Button
                  variant="primary"
                  size="sm"
                  type="button"
                  disabled={generating || !canBuildReport}
                  onClick={() => void handleGenerate()}
                  data-testid="ask-for-report-generate-btn"
                >
                  {generating
                    ? integrated
                      ? 'Loading preview…'
                      : 'Generating…'
                    : integrated
                      ? 'Preview in Report Builder'
                      : 'Generate Report'}
                </Button>
                {!integrated && onOpenInBuilder ? (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={() =>
                      onOpenInBuilder(effectivePlan || plan, {
                        answerText: answer?.text || '',
                        summary: answer?.summary || null,
                      })
                    }
                    data-testid="ask-for-report-open-builder-btn"
                  >
                    Edit columns &amp; filters
                  </Button>
                ) : null}
              </Stack>
              {!canBuildReport ? (
                <Alert
                  variant="warning"
                  className="mb-0 small"
                  data-testid="ask-for-report-unsupported"
                >
                  That answer could not be opened as an editable Report Builder
                  configuration with the available fields.
                </Alert>
              ) : null}
            </div>
          ) : null}

          {loadError ? (
            <Alert
              variant="danger"
              className="mt-3 mb-0"
              data-testid="ask-for-report-load-error"
            >
              {loadError}
            </Alert>
          ) : null}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  )
}
